import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, boolean, jsonb, index, doublePrecision, unique, pgEnum } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const facilityTypeEnum = pgEnum("facility_type", ["hospital", "pharmacy", "lab", "clinic", "other"]);

export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timestamp: timestamp("timestamp").defaultNow(),
  eventType: text("event_type").notNull(),
  severity: text("severity").notNull(),
  actor: text("actor").notNull(),
  action: text("action").notNull(),
  resourceType: text("resource_type").notNull(),
  resourceId: text("resource_id").notNull(),
  details: text("details"),
  ipAddress: text("ip_address"),
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({ id: true, timestamp: true });

export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

// ── In-app error tracking ─────────────────────────────────────────────────────

export const errorEvents = pgTable("error_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  environment: text("environment").notNull(),
  source: text("source").notNull(),
  level: text("level").notNull().default("error"),
  message: text("message").notNull(),
  stack: text("stack"),
  fingerprint: varchar("fingerprint", { length: 64 }).notNull(),
  url: text("url"),
  method: text("method"),
  statusCode: integer("status_code"),
  userAgent: text("user_agent"),
  userId: varchar("user_id"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  resolved: boolean("resolved").default(false).notNull(),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: varchar("resolved_by"),
  resolutionNote: text("resolution_note"),
}, (table) => [
  index("error_events_occurred_idx").on(table.occurredAt),
  index("error_events_fingerprint_idx").on(table.fingerprint),
  index("error_events_source_idx").on(table.source),
  index("error_events_unresolved_idx").on(table.resolved, table.occurredAt),
]);

export type ErrorEvent = typeof errorEvents.$inferSelect;

export const errorAlertLog = pgTable("error_alert_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  triggerCount: integer("trigger_count").notNull(),
  windowMinutes: integer("window_minutes").notNull().default(5),
  recipient: text("recipient").notNull(),
});

export type ErrorAlertLog = typeof errorAlertLog.$inferSelect;

export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: varchar("username").unique(),
  password: varchar("password"),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  role: varchar("role").default("dispatcher").notNull(),
  hospitalPartnerId: varchar("hospital_partner_id"),
  employerPartnerId: varchar("employer_partner_id"),
  language: varchar("language").default("en").notNull(),
  bio: text("bio"),
  phone: varchar("phone"),
  zipCode: varchar("zip_code"),
  supportInterests: text("support_interests").array(),
  notificationPreferences: text("notification_preferences").default("{}"),
  onboardingCompleted: boolean("onboarding_completed").default(false),
  needsActivation: boolean("needs_activation").default(false),
  isTest: boolean("is_test").default(false),
  deletedAt: timestamp("deleted_at"),
  deletedBy: text("deleted_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

export const insertUserSchema = createInsertSchema(users).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true,
  profileImageUrl: true,
  hospitalPartnerId: true,
  employerPartnerId: true,
  bio: true,
  phone: true,
  zipCode: true,
  supportInterests: true,
  notificationPreferences: true,
  onboardingCompleted: true,
}).extend({
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  email: z.string().email("Invalid email address"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  role: z.enum(["patient", "supporter", "driver", "dispatcher", "admin", "hospital_partner_admin", "employer_admin"]),
});

export type InsertUser = z.infer<typeof insertUserSchema>;

export const facilities = pgTable("facilities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  address: text("address").notNull(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zip: text("zip").notNull(),
  type: facilityTypeEnum("type").notNull(),
  lat: doublePrecision("lat"),
  lng: doublePrecision("lng"),
  notes: text("notes"),
  deletedAt: timestamp("deleted_at"),
  deletedBy: text("deleted_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("facility_name_idx").on(table.name),
  index("facility_city_state_idx").on(table.city, table.state),
]);

export const insertFacilitySchema = createInsertSchema(facilities).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertFacility = z.infer<typeof insertFacilitySchema>;
export type Facility = typeof facilities.$inferSelect;

// DiaspoCare Health Wallet - Patient and organization healthcare financing
// Wallet types: individual (single patient), family (household members), community (neighborhood/association), organization (church/mosque/cooperative)
export const walletTypeEnum = pgEnum("wallet_type", ["individual", "family", "community", "organization"]);
export const walletVerificationStatusEnum = pgEnum("wallet_verification_status", ["pending", "verified", "rejected"]);

export const wallets = pgTable("wallets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // primary owner/creator
  patientName: text("patient_name").notNull(), // for individual: patient name, for others: wallet display name
  balance: integer("balance").notNull().default(0), // in cents
  totalContributions: integer("total_contributions").notNull().default(0), // lifetime contributions in cents
  totalSpent: integer("total_spent").notNull().default(0), // lifetime spent in cents
  shareToken: varchar("share_token").unique(), // for shareable contribution links
  stripeCustomerId: varchar("stripe_customer_id"),
  category: text("category"), // disease category for grouping (e.g., "diabetes", "cancer", "pediatric")
  description: text("description"), // patient's story/situation or organization mission
  goalAmount: integer("goal_amount"), // funding goal in cents
  isPublic: boolean("is_public").default(false), // whether wallet is visible in community fund
  
  // Enhanced wallet type support
  walletType: text("wallet_type").notNull().default("individual"), // "individual", "family", "community", "organization", "condition"
  organizationName: text("organization_name"), // for community/organization wallets
  organizationType: text("organization_type"), // "church", "mosque", "cooperative", "association", "nonprofit", "other"
  taxId: text("tax_id"), // EIN for organizations (encrypted/masked)
  verificationStatus: text("verification_status").notNull().default("pending"), // "pending", "verified", "rejected"
  verifiedAt: timestamp("verified_at"),
  verifiedBy: text("verified_by"),
  
  // Governance settings
  requiresApprovalForPayments: boolean("requires_approval_for_payments").default(false), // multi-admin approval
  paymentApprovalThreshold: integer("payment_approval_threshold"), // amount in cents requiring approval
  maxBeneficiarySpendingLimit: integer("max_beneficiary_spending_limit"), // default per-beneficiary limit in cents
  
  joinToken: varchar("join_token").unique(), // reusable shareable link token for joining wallet as member/beneficiary
  joinTokenEnabled: boolean("join_token_enabled").default(false), // whether the join link is active
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("wallet_user_idx").on(table.userId),
  index("wallet_share_token_idx").on(table.shareToken),
  index("wallet_category_idx").on(table.category),
  index("wallet_type_idx").on(table.walletType),
  index("wallet_org_name_idx").on(table.organizationName),
  index("wallet_join_token_idx").on(table.joinToken),
]);

// Wallet transactions - both contributions and expenditures
export const walletTransactions = pgTable("wallet_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  type: text("type").notNull(), // "contribution", "order_payment", "refund", "subscription"
  amount: integer("amount").notNull(), // in cents, positive for credits, negative for debits
  description: text("description"),
  contributorName: text("contributor_name"), // for contributions from family/friends
  contributorEmail: text("contributor_email"),
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  stripeSubscriptionId: varchar("stripe_subscription_id"),
  status: text("status").notNull().default("completed"), // "pending", "completed", "failed", "refunded"
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("transaction_wallet_idx").on(table.walletId),
  index("transaction_stripe_idx").on(table.stripePaymentIntentId),
]);

// Recurring contribution subscriptions
export const walletSubscriptions = pgTable("wallet_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  stripeSubscriptionId: varchar("stripe_subscription_id").notNull(),
  stripePriceId: varchar("stripe_price_id").notNull(),
  amount: integer("amount").notNull(), // monthly amount in cents
  contributorName: text("contributor_name"),
  contributorEmail: text("contributor_email"),
  status: text("status").notNull().default("active"), // "active", "paused", "cancelled"
  createdAt: timestamp("created_at").defaultNow(),
  cancelledAt: timestamp("cancelled_at"),
}, (table) => [
  index("subscription_wallet_idx").on(table.walletId),
  index("subscription_stripe_idx").on(table.stripeSubscriptionId),
]);

// Community funds - pools for specific diseases/demographics
export const communityFunds = pgTable("community_funds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  category: text("category").notNull(), // "pediatric", "diabetes", "cancer", etc.
  balance: integer("balance").notNull().default(0), // in cents
  totalRaised: integer("total_raised").notNull().default(0),
  totalDistributed: integer("total_distributed").notNull().default(0),
  isActive: boolean("is_active").default(true),
  isTest: boolean("is_test").default(false), // true for seed/test data, filtered from public API

  // Multilingual support (matches medical_conditions pattern)
  nameEs: text("name_es"),
  nameSo: text("name_so"),
  nameHmn: text("name_hmn"),
  descriptionEs: text("description_es"),
  descriptionSo: text("description_so"),
  descriptionHmn: text("description_hmn"),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("fund_category_idx").on(table.category),
]);

// Fund allocations - distribution from community funds to patient wallets
export const fundAllocations = pgTable("fund_allocations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fundId: varchar("fund_id").notNull(),
  walletId: varchar("wallet_id").notNull(),
  amount: integer("amount").notNull(), // in cents
  reason: text("reason"),
  approvedBy: text("approved_by"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("allocation_fund_idx").on(table.fundId),
  index("allocation_wallet_idx").on(table.walletId),
]);

// Wallet Administrators - multi-admin support for family/community/organization wallets
export const walletAdministrators = pgTable("wallet_administrators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  userId: varchar("user_id").notNull(),
  role: text("role").notNull().default("admin"), // "owner", "admin", "contributor", "viewer"
  permissions: text("permissions").array(), // ["view", "contribute", "spend", "manage_beneficiaries", "approve_payments", "invite_admins"]
  invitedBy: varchar("invited_by"),
  invitedAt: timestamp("invited_at").defaultNow(),
  acceptedAt: timestamp("accepted_at"),
  status: text("status").notNull().default("pending"), // "pending", "active", "revoked"
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("wallet_admin_wallet_idx").on(table.walletId),
  index("wallet_admin_user_idx").on(table.userId),
]);

// Wallet Beneficiaries - individuals who can receive healthcare services funded by the wallet
export const walletBeneficiaries = pgTable("wallet_beneficiaries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  dateOfBirth: timestamp("date_of_birth"),
  relationship: text("relationship"), // "self", "spouse", "child", "parent", "member", "other"
  email: text("email"),
  phone: text("phone"),
  
  // Eligibility and limits
  isActive: boolean("is_active").default(true),
  spendingLimit: integer("spending_limit"), // per-beneficiary spending limit in cents (null = use wallet default)
  totalSpent: integer("total_spent").notNull().default(0), // lifetime spent for this beneficiary in cents
  eligibilityNotes: text("eligibility_notes"), // any special conditions/notes
  
  // Verification
  verificationStatus: text("verification_status").notNull().default("pending"), // "pending", "verified", "rejected"
  verifiedAt: timestamp("verified_at"),
  
  addedBy: varchar("added_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("beneficiary_wallet_idx").on(table.walletId),
  index("beneficiary_name_idx").on(table.firstName, table.lastName),
]);

// Wallet Invitations - email-based invitations to join wallets as admin or beneficiary
export const walletInvitations = pgTable("wallet_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  
  // Invitation details
  email: text("email").notNull(), // invitee's email
  inviteType: text("invite_type").notNull(), // "admin", "beneficiary", "contributor"
  role: text("role"), // specific role for admins: "admin", "contributor", "viewer"
  permissions: text("permissions").array(), // specific permissions to grant
  
  // Beneficiary-specific fields (used when inviteType = "beneficiary")
  beneficiaryFirstName: text("beneficiary_first_name"),
  beneficiaryLastName: text("beneficiary_last_name"),
  beneficiaryRelationship: text("beneficiary_relationship"), // "spouse", "child", "parent", "member", "other"
  beneficiarySpendingLimit: integer("beneficiary_spending_limit"), // in cents
  
  // Invitation state
  status: text("status").notNull().default("pending"), // "pending", "accepted", "declined", "expired", "revoked"
  inviteToken: varchar("invite_token").unique(), // secure token for accepting invitation
  message: text("message"), // optional personal message from inviter
  
  // Tracking
  invitedBy: varchar("invited_by").notNull(),
  invitedByName: text("invited_by_name"),
  invitedAt: timestamp("invited_at").defaultNow(),
  expiresAt: timestamp("expires_at"), // optional expiration
  acceptedAt: timestamp("accepted_at"),
  acceptedByUserId: varchar("accepted_by_user_id"), // user who accepted the invitation
  declinedAt: timestamp("declined_at"),
  revokedAt: timestamp("revoked_at"),
  revokedBy: varchar("revoked_by"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("wallet_invitation_wallet_idx").on(table.walletId),
  index("wallet_invitation_email_idx").on(table.email),
  index("wallet_invitation_token_idx").on(table.inviteToken),
  index("wallet_invitation_status_idx").on(table.status),
]);

// Healthcare Providers - hospitals, clinics, pharmacies that receive payments
export const healthcareProviders = pgTable("healthcare_providers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  providerType: text("provider_type").notNull(), // "hospital", "clinic", "health_center", "pharmacy", "lab", "specialist", "other"
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  phone: text("phone"),
  email: text("email"),
  website: text("website"),

  lat: doublePrecision("lat"),
  lng: doublePrecision("lng"),
  notes: text("notes"),

  // Payment information
  taxId: text("tax_id"), // provider EIN
  paymentMethod: text("payment_method").notNull().default("check"), // "check", "ach", "wire"
  bankName: text("bank_name"),
  bankRoutingNumber: text("bank_routing_number"), // encrypted
  bankAccountNumber: text("bank_account_number"), // encrypted
  payableTo: text("payable_to"), // name on check if different from provider name

  // Verification
  verificationStatus: text("verification_status").notNull().default("pending"), // "pending", "verified", "rejected"
  verifiedAt: timestamp("verified_at"),
  verifiedBy: text("verified_by"),

  isActive: boolean("is_active").default(true),
  deletedAt: timestamp("deleted_at"),
  deletedBy: text("deleted_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("provider_name_idx").on(table.name),
  index("provider_type_idx").on(table.providerType),
  index("provider_city_state_idx").on(table.city, table.state),
]);

// Provider Payments - disbursements from wallets to healthcare providers
export const providerPayments = pgTable("provider_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  providerId: varchar("provider_id").notNull(),
  beneficiaryId: varchar("beneficiary_id"), // optional link to specific beneficiary
  walletTransactionId: varchar("wallet_transaction_id"), // link to wallet transaction when payment is processed
  
  // Payment details
  amount: integer("amount").notNull(), // in cents
  serviceDate: timestamp("service_date"), // date of service
  serviceDescription: text("service_description"),
  invoiceNumber: text("invoice_number"),
  invoiceDate: timestamp("invoice_date"),
  
  // Approval workflow
  status: text("status").notNull().default("pending"), // "pending", "approved", "rejected", "processing", "completed", "failed"
  requestedBy: varchar("requested_by").notNull(),
  requestedAt: timestamp("requested_at").defaultNow(),
  approvedBy: varchar("approved_by"),
  approvedAt: timestamp("approved_at"),
  rejectedBy: varchar("rejected_by"),
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  
  // Processing details
  processedAt: timestamp("processed_at"),
  paymentReference: text("payment_reference"), // check number, ACH reference, etc.
  paymentMethod: text("payment_method"), // method used for this payment
  
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("provider_payment_wallet_idx").on(table.walletId),
  index("provider_payment_provider_idx").on(table.providerId),
  index("provider_payment_beneficiary_idx").on(table.beneficiaryId),
  index("provider_payment_status_idx").on(table.status),
]);

// ============================================
// Disease Condition-Based Wallet System
// Enables public exploration of condition wallets and community/institutional funding
// ============================================

// Medical Conditions - Master list of conditions for organizing wallets
export const medicalConditions = pgTable("medical_conditions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Condition identity
  slug: varchar("slug").unique().notNull(), // URL-friendly identifier e.g., "type-2-diabetes"
  name: text("name").notNull(), // Display name e.g., "Type 2 Diabetes"
  description: text("description"), // Brief description of the condition
  
  // Categorization
  category: text("category").notNull(), // e.g., "chronic", "cancer", "pediatric", "mental_health", "rare_disease"
  
  // Public visibility
  isActive: boolean("is_active").default(true),
  isFeatured: boolean("is_featured").default(false), // Featured on landing page
  displayOrder: integer("display_order").default(0),
  
  // Media
  iconName: text("icon_name"), // Lucide icon name
  coverImageUrl: text("cover_image_url"),
  
  // Multilingual support
  nameEs: text("name_es"), // Spanish
  nameSo: text("name_so"), // Somali
  nameHmn: text("name_hmn"), // Hmong
  descriptionEs: text("description_es"),
  descriptionSo: text("description_so"),
  descriptionHmn: text("description_hmn"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("condition_slug_idx").on(table.slug),
  index("condition_category_idx").on(table.category),
  index("condition_active_idx").on(table.isActive),
  index("condition_featured_idx").on(table.isFeatured),
]);

// Condition Wallets - Public-facing wrapper linking wallets to conditions
export const conditionWallets = pgTable("condition_wallets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(), // Links to existing wallet
  conditionId: varchar("condition_id").notNull(), // Links to medical_conditions
  
  // Public profile (for non-authenticated browsing)
  title: text("title").notNull(), // e.g., "Minneapolis Diabetes Care Fund"
  summary: text("summary"), // Brief public summary (no sensitive data)
  story: text("story"), // Longer narrative about impact and mission (no sensitive data)
  
  // Community Support Funding
  fundingGoal: integer("funding_goal"), // Target amount in cents
  isOpenForFunding: boolean("is_open_for_funding").default(true),
  
  // Media
  coverImageUrl: text("cover_image_url"),
  galleryImageUrls: text("gallery_image_urls").array(),
  
  // Visibility settings
  visibilityLevel: text("visibility_level").notNull().default("public"), // "public", "unlisted", "private"
  isFeatured: boolean("is_featured").default(false),
  displayOrder: integer("display_order").default(0),
  
  // Aggregate stats (cached for public display - no sensitive data)
  memberCount: integer("member_count").notNull().default(0),
  totalRaised: integer("total_raised").notNull().default(0), // in cents
  contributorCount: integer("contributor_count").notNull().default(0),
  servicesDelivered: integer("services_delivered").notNull().default(0),
  
  // Multilingual support
  titleEs: text("title_es"),
  titleSo: text("title_so"),
  titleHmn: text("title_hmn"),
  summaryEs: text("summary_es"),
  summarySo: text("summary_so"),
  summaryHmn: text("summary_hmn"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("condition_wallet_wallet_idx").on(table.walletId),
  index("condition_wallet_condition_idx").on(table.conditionId),
  index("condition_wallet_visibility_idx").on(table.visibilityLevel),
  index("condition_wallet_featured_idx").on(table.isFeatured),
  unique("condition_wallet_unique").on(table.walletId, table.conditionId),
]);

// Wallet Members - Patients who join condition-based wallets
export const walletMembers = pgTable("wallet_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(), // Links to wallet/condition_wallet
  conditionId: varchar("condition_id").notNull(), // Links to medical_conditions
  userId: varchar("user_id").notNull(), // Patient's user account
  
  // Membership status
  status: text("status").notNull().default("pending"), // "pending", "approved", "active", "suspended", "removed"
  
  // Consent tracking
  consentVersion: text("consent_version").notNull(), // Version of consent form signed
  consentSignedAt: timestamp("consent_signed_at").notNull(),
  consentIpAddress: text("consent_ip_address"),
  
  // Privacy preferences
  displayNamePublic: boolean("display_name_public").default(false), // Can show first name publicly
  shareStoryPublic: boolean("share_story_public").default(false), // Can share anonymized story
  
  // Clinical verification (encrypted/optional)
  verificationMethod: text("verification_method"), // "provider_attestation", "document_upload", "self_declaration"
  verificationStatus: text("verification_status").notNull().default("pending"), // "pending", "verified", "rejected"
  verifiedAt: timestamp("verified_at"),
  verifiedBy: varchar("verified_by"),
  
  // Benefits received
  totalBenefitsReceived: integer("total_benefits_received").notNull().default(0), // in cents
  servicesReceived: integer("services_received").notNull().default(0),
  
  // Application details
  applicationNotes: text("application_notes"), // Why they need help (optional)
  pledgedMonthlyAmountCents: integer("pledged_monthly_amount_cents").default(0), // Monthly recurring pledge (0 = membership only)

  joinedAt: timestamp("joined_at").defaultNow(),
  approvedAt: timestamp("approved_at"),
  approvedBy: varchar("approved_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("wallet_member_wallet_idx").on(table.walletId),
  index("wallet_member_condition_idx").on(table.conditionId),
  index("wallet_member_user_idx").on(table.userId),
  index("wallet_member_status_idx").on(table.status),
  unique("wallet_member_unique").on(table.walletId, table.userId),
]);

// Wallet Contributors - Tracks contributions with privacy preferences
export const walletContributors = pgTable("wallet_contributors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(), // Links to wallet/condition_wallet
  conditionWalletId: varchar("condition_wallet_id"), // Optional direct link to condition wallet
  
  // Contributor identity (optional for anonymous)
  userId: varchar("user_id"), // Null for anonymous contributors
  contributorType: text("contributor_type").notNull().default("individual"), // "individual", "institution", "corporate", "foundation"
  
  // Public display (controlled by contributor)
  displayName: text("display_name"), // Name to show publicly (if not anonymous)
  organizationName: text("organization_name"), // For institutional contributors
  isAnonymous: boolean("is_anonymous").default(false),
  
  // Contact (encrypted - for tax receipts, updates)
  contactEmail: text("contact_email"),
  
  // Aggregate contribution stats
  totalContributed: integer("total_contributed").notNull().default(0), // in cents
  contributionCount: integer("contribution_count").notNull().default(0),
  lastContributionAt: timestamp("last_contribution_at"),
  
  // Visibility preferences
  showOnLeaderboard: boolean("show_on_leaderboard").default(false),
  showContributionAmount: boolean("show_contribution_amount").default(false),
  
  // Optional public message
  publicMessage: text("public_message"), // Message to show on wallet page
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("wallet_contributor_wallet_idx").on(table.walletId),
  index("wallet_contributor_condition_wallet_idx").on(table.conditionWalletId),
  index("wallet_contributor_user_idx").on(table.userId),
  index("wallet_contributor_type_idx").on(table.contributorType),
]);

// Insert schemas and types for condition-based wallet system
export const insertMedicalConditionSchema = createInsertSchema(medicalConditions).omit({ 
  id: true, createdAt: true, updatedAt: true 
});
export const insertConditionWalletSchema = createInsertSchema(conditionWallets).omit({ 
  id: true, createdAt: true, updatedAt: true, 
  memberCount: true, totalRaised: true, contributorCount: true, servicesDelivered: true 
});
export const insertWalletMemberSchema = createInsertSchema(walletMembers).omit({ 
  id: true, createdAt: true, updatedAt: true, joinedAt: true, approvedAt: true, approvedBy: true,
  verifiedAt: true, verifiedBy: true, totalBenefitsReceived: true, servicesReceived: true
});
export const insertWalletContributorSchema = createInsertSchema(walletContributors).omit({ 
  id: true, createdAt: true, updatedAt: true, 
  totalContributed: true, contributionCount: true, lastContributionAt: true 
});

export type InsertMedicalCondition = z.infer<typeof insertMedicalConditionSchema>;
export type MedicalCondition = typeof medicalConditions.$inferSelect;
export type InsertConditionWallet = z.infer<typeof insertConditionWalletSchema>;
export type ConditionWallet = typeof conditionWallets.$inferSelect;
export type InsertWalletMember = z.infer<typeof insertWalletMemberSchema>;
export type WalletMember = typeof walletMembers.$inferSelect;
export type InsertWalletContributor = z.infer<typeof insertWalletContributorSchema>;
export type WalletContributor = typeof walletContributors.$inferSelect;

// ============================================
// Condition Wallet Admin System
// Management, financial tracking, and compliance for condition-based wallets
// ============================================

// Condition Categories - For organizing medical conditions into broader groups
export const conditionCategories = pgTable("condition_categories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  slug: varchar("slug").unique().notNull(),
  description: text("description"),
  displayOrder: integer("display_order").default(0),
  isActive: boolean("is_active").default(true),
  iconName: text("icon_name"),
  colorHex: text("color_hex"), // UI color for category badges
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("condition_category_slug_idx").on(table.slug),
  index("condition_category_active_idx").on(table.isActive),
]);

// Wallet Disbursements - Tracks fund releases to beneficiaries
export const walletDisbursements = pgTable("wallet_disbursements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conditionWalletId: varchar("condition_wallet_id").notNull(),
  memberId: varchar("member_id").notNull(), // wallet_members.id
  
  // Amount and purpose
  amount: integer("amount").notNull(), // in cents
  purpose: text("purpose").notNull(), // "medication", "treatment", "equipment", "supplies", "other"
  description: text("description"),
  
  // Approval workflow
  status: text("status").notNull().default("pending"), // "pending", "approved", "processing", "completed", "rejected", "cancelled"
  requestedBy: varchar("requested_by").notNull(), // user who initiated
  requestedAt: timestamp("requested_at").defaultNow(),
  
  approvedBy: varchar("approved_by"),
  approvedAt: timestamp("approved_at"),
  approvalNotes: text("approval_notes"),
  
  rejectedBy: varchar("rejected_by"),
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  
  // Processing details
  processedAt: timestamp("processed_at"),
  paymentMethod: text("payment_method"), // "direct", "check", "provider_payment"
  paymentReference: text("payment_reference"), // check number, transaction ID, etc.
  
  // Linked records
  providerPaymentId: varchar("provider_payment_id"), // if paid to a provider
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("disbursement_wallet_idx").on(table.conditionWalletId),
  index("disbursement_member_idx").on(table.memberId),
  index("disbursement_status_idx").on(table.status),
  index("disbursement_requested_at_idx").on(table.requestedAt),
]);

// Wallet Refunds - Tracks contributor refund requests and processing
export const walletRefunds = pgTable("wallet_refunds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conditionWalletId: varchar("condition_wallet_id").notNull(),
  contributorId: varchar("contributor_id").notNull(), // wallet_contributors.id
  
  // Refund details
  originalAmount: integer("original_amount").notNull(), // original contribution in cents
  refundAmount: integer("refund_amount").notNull(), // amount to refund in cents
  reason: text("reason").notNull(), // "contributor_request", "error", "fraud", "wallet_closed", "other"
  reasonDetails: text("reason_details"),
  
  // Status tracking
  status: text("status").notNull().default("pending"), // "pending", "approved", "processing", "completed", "rejected"
  
  requestedBy: varchar("requested_by").notNull(),
  requestedAt: timestamp("requested_at").defaultNow(),
  
  approvedBy: varchar("approved_by"),
  approvedAt: timestamp("approved_at"),
  
  rejectedBy: varchar("rejected_by"),
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  
  // Stripe refund details
  stripeRefundId: varchar("stripe_refund_id"),
  stripeStatus: text("stripe_status"), // Stripe refund status
  processedAt: timestamp("processed_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("refund_wallet_idx").on(table.conditionWalletId),
  index("refund_contributor_idx").on(table.contributorId),
  index("refund_status_idx").on(table.status),
]);

// Wallet Ledger Entries - Double-entry accounting for fund tracking
export const walletLedgerEntries = pgTable("wallet_ledger_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conditionWalletId: varchar("condition_wallet_id").notNull(),
  
  // Entry type and direction
  entryType: text("entry_type").notNull(), // "contribution", "disbursement", "refund", "fee", "adjustment", "transfer"
  direction: text("direction").notNull(), // "credit" (inflow) or "debit" (outflow)
  
  // Amounts
  amount: integer("amount").notNull(), // in cents (always positive)
  balanceAfter: integer("balance_after").notNull(), // wallet balance after this entry
  
  // References
  referenceType: text("reference_type"), // "contribution", "disbursement", "refund", "order"
  referenceId: varchar("reference_id"), // ID of the source record
  
  // Description
  description: text("description"),
  notes: text("notes"),
  
  // Actor
  createdBy: varchar("created_by"),
  
  // Reconciliation
  isReconciled: boolean("is_reconciled").default(false),
  reconciledAt: timestamp("reconciled_at"),
  reconciledBy: varchar("reconciled_by"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("ledger_wallet_idx").on(table.conditionWalletId),
  index("ledger_type_idx").on(table.entryType),
  index("ledger_direction_idx").on(table.direction),
  index("ledger_created_idx").on(table.createdAt),
  index("ledger_reference_idx").on(table.referenceType, table.referenceId),
]);

// Condition Wallet Admin Events - Comprehensive audit trail
export const conditionWalletAdminEvents = pgTable("condition_wallet_admin_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // What was affected
  entityType: text("entity_type").notNull(), // "condition", "wallet", "member", "contributor", "disbursement", "refund"
  entityId: varchar("entity_id").notNull(),
  conditionWalletId: varchar("condition_wallet_id"), // Optional, for context
  
  // What happened
  action: text("action").notNull(), // "create", "update", "delete", "approve", "reject", "archive", "restore", "feature", "unfeature"
  
  // Change details (no sensitive data in snapshots)
  fieldChanges: jsonb("field_changes"), // { field: { old: value, new: value } }
  
  // Actor and context
  actorId: varchar("actor_id").notNull(),
  actorEmail: text("actor_email"),
  actorRole: text("actor_role"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  
  // Additional context
  reason: text("reason"), // Why the action was taken
  metadata: jsonb("metadata"), // Additional structured data
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("admin_event_entity_idx").on(table.entityType, table.entityId),
  index("admin_event_wallet_idx").on(table.conditionWalletId),
  index("admin_event_action_idx").on(table.action),
  index("admin_event_actor_idx").on(table.actorId),
  index("admin_event_created_idx").on(table.createdAt),
]);

// Wallet Goal History - Track funding goal changes
export const walletGoalHistory = pgTable("wallet_goal_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conditionWalletId: varchar("condition_wallet_id").notNull(),
  previousGoal: integer("previous_goal"), // in cents
  newGoal: integer("new_goal").notNull(), // in cents
  reason: text("reason"),
  changedBy: varchar("changed_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("goal_history_wallet_idx").on(table.conditionWalletId),
]);

// Wallet Feature History - Track featured/unfeatured changes
export const walletFeatureHistory = pgTable("wallet_feature_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conditionWalletId: varchar("condition_wallet_id").notNull(),
  isFeatured: boolean("is_featured").notNull(),
  startDate: timestamp("start_date").defaultNow(),
  endDate: timestamp("end_date"),
  reason: text("reason"),
  changedBy: varchar("changed_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("feature_history_wallet_idx").on(table.conditionWalletId),
]);

// Member Status History - Track membership status changes
export const memberStatusHistory = pgTable("member_status_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  memberId: varchar("member_id").notNull(),
  previousStatus: text("previous_status"),
  newStatus: text("new_status").notNull(),
  reason: text("reason"),
  changedBy: varchar("changed_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("member_status_history_member_idx").on(table.memberId),
]);

// Insert schemas and types for admin system
export const insertConditionCategorySchema = createInsertSchema(conditionCategories).omit({ 
  id: true, createdAt: true, updatedAt: true 
});
export const insertWalletDisbursementSchema = createInsertSchema(walletDisbursements).omit({ 
  id: true, createdAt: true, updatedAt: true, requestedAt: true, approvedAt: true, rejectedAt: true, processedAt: true 
});
export const insertWalletRefundSchema = createInsertSchema(walletRefunds).omit({ 
  id: true, createdAt: true, updatedAt: true, requestedAt: true, approvedAt: true, rejectedAt: true, processedAt: true 
});
export const insertWalletLedgerEntrySchema = createInsertSchema(walletLedgerEntries).omit({ 
  id: true, createdAt: true, reconciledAt: true 
});
export const insertConditionWalletAdminEventSchema = createInsertSchema(conditionWalletAdminEvents).omit({ 
  id: true, createdAt: true 
});
export const insertWalletGoalHistorySchema = createInsertSchema(walletGoalHistory).omit({ 
  id: true, createdAt: true 
});
export const insertWalletFeatureHistorySchema = createInsertSchema(walletFeatureHistory).omit({ 
  id: true, createdAt: true, endDate: true 
});
export const insertMemberStatusHistorySchema = createInsertSchema(memberStatusHistory).omit({ 
  id: true, createdAt: true 
});

export type InsertConditionCategory = z.infer<typeof insertConditionCategorySchema>;
export type ConditionCategory = typeof conditionCategories.$inferSelect;
export type InsertWalletDisbursement = z.infer<typeof insertWalletDisbursementSchema>;
export type WalletDisbursement = typeof walletDisbursements.$inferSelect;
export type InsertWalletRefund = z.infer<typeof insertWalletRefundSchema>;
export type WalletRefund = typeof walletRefunds.$inferSelect;
export type InsertWalletLedgerEntry = z.infer<typeof insertWalletLedgerEntrySchema>;
export type WalletLedgerEntry = typeof walletLedgerEntries.$inferSelect;
export type InsertConditionWalletAdminEvent = z.infer<typeof insertConditionWalletAdminEventSchema>;
export type ConditionWalletAdminEvent = typeof conditionWalletAdminEvents.$inferSelect;
export type InsertWalletGoalHistory = z.infer<typeof insertWalletGoalHistorySchema>;
export type WalletGoalHistory = typeof walletGoalHistory.$inferSelect;
export type InsertWalletFeatureHistory = z.infer<typeof insertWalletFeatureHistorySchema>;
export type WalletFeatureHistory = typeof walletFeatureHistory.$inferSelect;
export type InsertMemberStatusHistory = z.infer<typeof insertMemberStatusHistorySchema>;
export type MemberStatusHistory = typeof memberStatusHistory.$inferSelect;

export const insertWalletSchema = createInsertSchema(wallets).omit({ id: true, createdAt: true, updatedAt: true, balance: true, totalContributions: true, totalSpent: true, verifiedAt: true, verifiedBy: true });
export const insertWalletTransactionSchema = createInsertSchema(walletTransactions).omit({ id: true, createdAt: true });
export const insertWalletSubscriptionSchema = createInsertSchema(walletSubscriptions).omit({ id: true, createdAt: true });
export const insertCommunityFundSchema = createInsertSchema(communityFunds).omit({ id: true, createdAt: true, updatedAt: true, balance: true, totalRaised: true, totalDistributed: true });
export const insertFundAllocationSchema = createInsertSchema(fundAllocations).omit({ id: true, createdAt: true });
export const insertWalletAdministratorSchema = createInsertSchema(walletAdministrators).omit({ id: true, createdAt: true, invitedAt: true, acceptedAt: true });
export const insertWalletBeneficiarySchema = createInsertSchema(walletBeneficiaries).omit({ id: true, createdAt: true, updatedAt: true, totalSpent: true, verifiedAt: true });
export const insertWalletInvitationSchema = createInsertSchema(walletInvitations).omit({ id: true, createdAt: true, updatedAt: true, invitedAt: true, acceptedAt: true, declinedAt: true, revokedAt: true });
export const insertHealthcareProviderSchema = createInsertSchema(healthcareProviders).omit({ id: true, createdAt: true, updatedAt: true, verifiedAt: true, verifiedBy: true, deletedAt: true, deletedBy: true });
export const insertProviderPaymentSchema = createInsertSchema(providerPayments).omit({ id: true, createdAt: true, updatedAt: true, requestedAt: true, approvedAt: true, rejectedAt: true, processedAt: true });

export type InsertWallet = z.infer<typeof insertWalletSchema>;
export type Wallet = typeof wallets.$inferSelect;
export type InsertWalletTransaction = z.infer<typeof insertWalletTransactionSchema>;
export type WalletTransaction = typeof walletTransactions.$inferSelect;
export type InsertWalletSubscription = z.infer<typeof insertWalletSubscriptionSchema>;
export type WalletSubscription = typeof walletSubscriptions.$inferSelect;
export type InsertCommunityFund = z.infer<typeof insertCommunityFundSchema>;
export type CommunityFund = typeof communityFunds.$inferSelect;
export type InsertFundAllocation = z.infer<typeof insertFundAllocationSchema>;
export type FundAllocation = typeof fundAllocations.$inferSelect;
export type InsertWalletAdministrator = z.infer<typeof insertWalletAdministratorSchema>;
export type WalletAdministrator = typeof walletAdministrators.$inferSelect;
export type InsertWalletBeneficiary = z.infer<typeof insertWalletBeneficiarySchema>;
export type WalletBeneficiary = typeof walletBeneficiaries.$inferSelect;
export type InsertWalletInvitation = z.infer<typeof insertWalletInvitationSchema>;
export type WalletInvitation = typeof walletInvitations.$inferSelect;
export type InsertHealthcareProvider = z.infer<typeof insertHealthcareProviderSchema>;
export type HealthcareProvider = typeof healthcareProviders.$inferSelect;
export type InsertProviderPayment = z.infer<typeof insertProviderPaymentSchema>;
export type ProviderPayment = typeof providerPayments.$inferSelect;

// System Settings - platform-wide configuration
export const systemSettings = pgTable("system_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  value: text("value").notNull(),
  category: text("category").notNull(), // "system", "display", "notifications"
  description: text("description"),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: text("updated_by"),
});

export const insertSystemSettingSchema = createInsertSchema(systemSettings).omit({ id: true, updatedAt: true });
export type InsertSystemSetting = z.infer<typeof insertSystemSettingSchema>;
export type SystemSetting = typeof systemSettings.$inferSelect;

// ============================================
// Post-Care Payment Schedules - Hospital-facilitated payment arrangements
// ============================================

export const paymentPlanStatusEnum = pgEnum("payment_plan_status", ["active", "paused", "completed", "defaulted", "cancelled"]);
export const paymentFrequencyEnum = pgEnum("payment_frequency", ["weekly", "biweekly", "monthly"]);

export const paymentPlans = pgTable("payment_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(), // linked to patient wallet
  userId: varchar("user_id").notNull(), // patient user ID
  
  // Plan details
  hospitalName: text("hospital_name").notNull(), // hospital/facility name for the bill
  serviceDescription: text("service_description"), // what the bill is for
  originalBalance: integer("original_balance").notNull(), // original amount owed in cents
  remainingBalance: integer("remaining_balance").notNull(), // current remaining balance in cents
  totalPaid: integer("total_paid").notNull().default(0), // total amount paid so far in cents
  
  // Payment schedule
  paymentAmount: integer("payment_amount").notNull(), // scheduled payment amount in cents
  frequency: paymentFrequencyEnum("frequency").notNull().default("monthly"),
  paymentDayOfWeek: integer("payment_day_of_week"), // 0-6 for weekly/biweekly (0=Sunday)
  paymentDayOfMonth: integer("payment_day_of_month"), // 1-28 for monthly payments
  durationMonths: integer("duration_months").notNull(), // total plan duration in months (up to 60 for 5 years)
  nextPaymentDate: timestamp("next_payment_date"),
  
  // Status and tracking
  status: paymentPlanStatusEnum("status").notNull().default("active"),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date"), // projected end date
  completedAt: timestamp("completed_at"), // when plan was fully paid
  
  // Hardship management
  isPaused: boolean("is_paused").default(false),
  pausedAt: timestamp("paused_at"),
  pauseReason: text("pause_reason"),
  pauseEndDate: timestamp("pause_end_date"), // when pause should automatically end
  totalPauseDays: integer("total_pause_days").default(0), // cumulative pause days (max 90)
  
  // Auto-payment settings
  autoPayEnabled: boolean("auto_pay_enabled").default(true),
  paymentMethod: text("payment_method"), // "wallet", "ach", "card"
  stripePaymentMethodId: varchar("stripe_payment_method_id"),
  
  // Metadata
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: text("created_by"), // who set up the plan
}, (table) => [
  index("payment_plan_wallet_idx").on(table.walletId),
  index("payment_plan_user_idx").on(table.userId),
  index("payment_plan_status_idx").on(table.status),
  index("payment_plan_next_payment_idx").on(table.nextPaymentDate),
]);

export const paymentPlanPayments = pgTable("payment_plan_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planId: varchar("plan_id").notNull(),
  
  // Payment details
  amount: integer("amount").notNull(), // payment amount in cents
  scheduledDate: timestamp("scheduled_date").notNull(), // when payment was due
  paidAt: timestamp("paid_at"), // when payment was actually made
  
  // Status
  status: text("status").notNull().default("scheduled"), // "scheduled", "pending", "completed", "failed", "missed", "skipped"
  
  // Payment processing
  paymentMethod: text("payment_method"), // "wallet", "ach", "card", "manual"
  walletTransactionId: varchar("wallet_transaction_id"), // if paid from wallet
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  
  // Tracking
  failureReason: text("failure_reason"), // if payment failed
  retryCount: integer("retry_count").default(0),
  notes: text("notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("payment_plan_payment_plan_idx").on(table.planId),
  index("payment_plan_payment_status_idx").on(table.status),
  index("payment_plan_payment_scheduled_idx").on(table.scheduledDate),
]);

// Hardship requests for plan modifications
export const paymentPlanHardshipRequests = pgTable("payment_plan_hardship_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planId: varchar("plan_id").notNull(),
  userId: varchar("user_id").notNull(),
  
  // Request details
  requestType: text("request_type").notNull(), // "pause", "reduce_payment", "extend_duration"
  reason: text("reason").notNull(),
  requestedPauseDays: integer("requested_pause_days"), // for pause requests
  requestedNewPayment: integer("requested_new_payment"), // for payment reduction
  requestedExtensionMonths: integer("requested_extension_months"), // for duration extension
  
  // Status
  status: text("status").notNull().default("pending"), // "pending", "approved", "denied"
  reviewedBy: text("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("hardship_request_plan_idx").on(table.planId),
  index("hardship_request_status_idx").on(table.status),
]);

export const insertPaymentPlanSchema = createInsertSchema(paymentPlans).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  totalPaid: true,
  completedAt: true,
  pausedAt: true,
  totalPauseDays: true,
});

export const insertPaymentPlanPaymentSchema = createInsertSchema(paymentPlanPayments).omit({ 
  id: true, 
  createdAt: true,
  retryCount: true,
});

export const insertHardshipRequestSchema = createInsertSchema(paymentPlanHardshipRequests).omit({
  id: true,
  createdAt: true,
  reviewedBy: true,
  reviewedAt: true,
  reviewNotes: true,
  status: true,
});

export type InsertPaymentPlan = z.infer<typeof insertPaymentPlanSchema>;
export type PaymentPlan = typeof paymentPlans.$inferSelect;
export type InsertPaymentPlanPayment = z.infer<typeof insertPaymentPlanPaymentSchema>;
export type PaymentPlanPayment = typeof paymentPlanPayments.$inferSelect;
export type InsertHardshipRequest = z.infer<typeof insertHardshipRequestSchema>;
export type HardshipRequest = typeof paymentPlanHardshipRequests.$inferSelect;

// ============================================
// Hardship Assistance Requests - General patient hardship (not plan-specific)
// ============================================
export const hardshipAssistanceRequests = pgTable("hardship_assistance_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  walletId: varchar("wallet_id"),
  reason: text("reason").notNull(),
  amountRequestedCents: integer("amount_requested_cents").notNull(),
  supportingDetails: text("supporting_details"),
  status: text("status").notNull().default("pending"),
  adminNote: text("admin_note"),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertHardshipAssistanceRequestSchema = createInsertSchema(hardshipAssistanceRequests).omit({
  id: true,
  status: true,
  adminNote: true,
  reviewedBy: true,
  reviewedAt: true,
  createdAt: true,
});

export type InsertHardshipAssistanceRequest = z.infer<typeof insertHardshipAssistanceRequestSchema>;
export type HardshipAssistanceRequest = typeof hardshipAssistanceRequests.$inferSelect;

// ============================================
// Hospital Partners - Partner relationship management
// ============================================

export const hospitalPartnerStatusEnum = pgEnum("hospital_partner_status", ["active", "pending", "suspended", "inactive"]);

export const hospitalPartners = pgTable("hospital_partners", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  facilityId: varchar("facility_id"), // deprecated: legacy link to facilities table
  healthcareProviderId: varchar("healthcare_provider_id"), // links to healthcare_providers (unified provider)
  
  // Partner details
  partnerName: text("partner_name").notNull(),
  partnerCode: varchar("partner_code").unique(), // unique identifier for the partnership
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  
  // Partnership terms
  status: hospitalPartnerStatusEnum("status").notNull().default("pending"),
  contractStartDate: timestamp("contract_start_date"),
  contractEndDate: timestamp("contract_end_date"),
  paymentTermsDays: integer("payment_terms_days").default(30), // NET 30, 60, 90
  
  // Revenue sharing
  revenueSharePercent: doublePrecision("revenue_share_percent").default(0), // partner's share of payment schedule revenue
  monthlyMinimumOrders: integer("monthly_minimum_orders").default(0),
  
  // Statistics (denormalized for performance)
  totalOrders: integer("total_orders").default(0),
  totalDeliveries: integer("total_deliveries").default(0),
  totalPaymentPlans: integer("total_payment_plans").default(0),
  totalRevenue: integer("total_revenue").default(0), // in cents
  
  // Notes
  notes: text("notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: text("created_by"),
  deletedAt: timestamp("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => [
  index("hospital_partner_facility_idx").on(table.facilityId),
  index("hospital_partner_status_idx").on(table.status),
  index("hospital_partner_code_idx").on(table.partnerCode),
  index("hospital_partner_provider_idx").on(table.healthcareProviderId),
]);

// Hospital partner monthly stats for reporting
export const hospitalPartnerStats = pgTable("hospital_partner_stats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(),
  
  // Period
  year: integer("year").notNull(),
  month: integer("month").notNull(),
  
  // Order metrics
  ordersCreated: integer("orders_created").default(0),
  ordersDelivered: integer("orders_delivered").default(0),
  ordersOnTime: integer("orders_on_time").default(0),
  
  // Payment schedule metrics
  paymentPlansCreated: integer("payment_plans_created").default(0),
  paymentPlansCompleted: integer("payment_plans_completed").default(0),
  paymentCollected: integer("payment_collected").default(0), // in cents
  
  // Performance metrics
  avgDeliveryTime: integer("avg_delivery_time"), // in minutes
  tempExcursions: integer("temp_excursions").default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("partner_stats_partner_idx").on(table.partnerId),
  unique("partner_stats_unique").on(table.partnerId, table.year, table.month),
]);

export const insertHospitalPartnerSchema = createInsertSchema(hospitalPartners).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  totalOrders: true,
  totalDeliveries: true,
  totalPaymentPlans: true,
  totalRevenue: true,
});

export const insertHospitalPartnerStatsSchema = createInsertSchema(hospitalPartnerStats).omit({ 
  id: true, 
  createdAt: true,
});

export type InsertHospitalPartner = z.infer<typeof insertHospitalPartnerSchema>;
export type HospitalPartner = typeof hospitalPartners.$inferSelect;
export type InsertHospitalPartnerStats = z.infer<typeof insertHospitalPartnerStatsSchema>;
export type HospitalPartnerStats = typeof hospitalPartnerStats.$inferSelect;

// Hospital partner invitations - for inviting hospital admins
export const hospitalPartnerInvitations = pgTable("hospital_partner_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(), // links to hospital_partners table
  
  // Invitee details
  email: text("email").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  title: text("title"), // job title at hospital
  department: text("department"), // department at hospital
  
  // Invitation state
  status: text("status").notNull().default("pending"), // "pending", "accepted", "expired", "revoked"
  inviteToken: varchar("invite_token").unique(), // secure token for accepting invitation
  message: text("message"), // optional personal message from inviter
  
  // Tracking
  invitedBy: varchar("invited_by").notNull(), // admin user who sent invitation
  invitedByName: text("invited_by_name"),
  invitedAt: timestamp("invited_at").defaultNow(),
  expiresAt: timestamp("expires_at"), // invitation expiration
  acceptedAt: timestamp("accepted_at"),
  acceptedByUserId: varchar("accepted_by_user_id"), // user ID who accepted
  revokedAt: timestamp("revoked_at"),
  revokedBy: varchar("revoked_by"),
  
  // Audit
  lastEmailSentAt: timestamp("last_email_sent_at"),
  emailSentCount: integer("email_sent_count").default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("hospital_partner_invitation_partner_idx").on(table.partnerId),
  index("hospital_partner_invitation_email_idx").on(table.email),
  index("hospital_partner_invitation_token_idx").on(table.inviteToken),
  index("hospital_partner_invitation_status_idx").on(table.status),
]);

export const insertHospitalPartnerInvitationSchema = createInsertSchema(hospitalPartnerInvitations).omit({ 
  id: true, 
  createdAt: true,
  updatedAt: true,
  acceptedAt: true,
  acceptedByUserId: true,
  revokedAt: true,
  revokedBy: true,
  emailSentCount: true,
});

export type InsertHospitalPartnerInvitation = z.infer<typeof insertHospitalPartnerInvitationSchema>;
export type HospitalPartnerInvitation = typeof hospitalPartnerInvitations.$inferSelect;

// ============================================
// TIER 2: COMPETITIVE MOATS - Pre-Care Subscriptions
// ============================================

export const preCareSubscriptionStatusEnum = pgEnum("precare_subscription_status", ["active", "paused", "cancelled"]);

export const preCareSubscriptions = pgTable("precare_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").notNull(),
  userId: varchar("user_id").notNull(),
  
  // Subscription details
  planTier: text("plan_tier").notNull().default("bronze"),
  monthlyAmount: integer("monthly_amount").notNull(),
  frequency: text("frequency").notNull().default("monthly"),
  paymentDayOfMonth: integer("payment_day_of_month").notNull().default(1),
  autopayEnabled: boolean("autopay_enabled").notNull().default(true),
  
  // Payment method
  paymentMethod: text("payment_method").notNull().default("card"),
  stripeSubscriptionId: varchar("stripe_subscription_id"),
  stripePaymentMethodId: varchar("stripe_payment_method_id"),
  
  // Status
  status: preCareSubscriptionStatusEnum("status").notNull().default("active"),
  pausedAt: timestamp("paused_at"),
  pauseReason: text("pause_reason"),
  pauseEndDate: timestamp("pause_end_date"),
  
  // Statistics
  totalContributed: integer("total_contributed").notNull().default(0),
  paymentsCount: integer("payments_count").notNull().default(0),
  lastPaymentDate: timestamp("last_payment_date"),
  nextPaymentDate: timestamp("next_payment_date"),
  
  // Metadata
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  cancelledAt: timestamp("cancelled_at"),
}, (table) => [
  index("precare_sub_wallet_idx").on(table.walletId),
  index("precare_sub_user_idx").on(table.userId),
  index("precare_sub_status_idx").on(table.status),
]);

export const preCareSubscriptionPayments = pgTable("precare_subscription_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subscriptionId: varchar("subscription_id").notNull(),
  walletId: varchar("wallet_id").notNull(),
  amount: integer("amount").notNull(),
  status: text("status").notNull().default("completed"),
  stripePaymentId: varchar("stripe_payment_id"),
  failureReason: text("failure_reason"),
  processedAt: timestamp("processed_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("precare_payment_sub_idx").on(table.subscriptionId),
  index("precare_payment_wallet_idx").on(table.walletId),
]);

// ============================================
// TIER 2: COMPETITIVE MOATS - Referral System
// ============================================

export const referralCodes = pgTable("referral_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  code: varchar("code").notNull().unique(), // e.g., "JOHN2024" or auto-generated
  
  // Reward settings
  referrerBonus: integer("referrer_bonus").notNull().default(2500), // $25 in cents for referrer
  refereeBonus: integer("referee_bonus").notNull().default(1000), // $10 in cents for new user
  
  // Statistics
  timesUsed: integer("times_used").notNull().default(0),
  totalBonusesEarned: integer("total_bonuses_earned").notNull().default(0),
  
  // Status
  isActive: boolean("is_active").default(true),
  maxUses: integer("max_uses"), // null = unlimited
  expiresAt: timestamp("expires_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("referral_code_user_idx").on(table.userId),
  index("referral_code_code_idx").on(table.code),
]);

export const referrals = pgTable("referrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referralCodeId: varchar("referral_code_id").notNull(),
  referrerId: varchar("referrer_id").notNull(), // who referred
  refereeId: varchar("referee_id").notNull(), // who was referred
  
  // Status tracking
  status: text("status").notNull().default("pending"), // "pending", "qualified", "paid", "expired"
  
  // Qualification criteria
  refereeWalletCreated: boolean("referee_wallet_created").default(false),
  refereeFirstPayment: boolean("referee_first_payment").default(false),
  qualifiedAt: timestamp("qualified_at"),
  
  // Bonus tracking
  referrerBonusPaid: boolean("referrer_bonus_paid").default(false),
  refereeBonusPaid: boolean("referee_bonus_paid").default(false),
  referrerBonusAmount: integer("referrer_bonus_amount"),
  refereeBonusAmount: integer("referee_bonus_amount"),
  bonusPaidAt: timestamp("bonus_paid_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("referral_referrer_idx").on(table.referrerId),
  index("referral_referee_idx").on(table.refereeId),
  index("referral_code_idx").on(table.referralCodeId),
  index("referral_status_idx").on(table.status),
]);

// ============================================
// TIER 2: COMPETITIVE MOATS - Health Engagement & Content
// ============================================

export const engagementContent = pgTable("engagement_content", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Content details
  type: text("type").notNull(), // "health_tip", "reminder", "education", "prevention"
  category: text("category"), // "diabetes", "pediatric", "prenatal", "general"
  title: text("title").notNull(),
  content: text("content").notNull(),
  
  // Multi-language support
  language: varchar("language").notNull().default("en"), // "en", "es", "so", "hmn"
  
  // Targeting
  targetAudience: text("target_audience"), // "all", "parents", "seniors", "chronic_conditions"
  
  // Scheduling
  frequency: text("frequency"), // "daily", "weekly", "monthly", "one_time"
  isActive: boolean("is_active").default(true),
  
  // Metadata
  author: text("author"),
  sourceUrl: text("source_url"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("engagement_content_type_idx").on(table.type),
  index("engagement_content_language_idx").on(table.language),
  index("engagement_content_category_idx").on(table.category),
]);

export const engagementDeliveries = pgTable("engagement_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contentId: varchar("content_id").notNull(),
  userId: varchar("user_id").notNull(),
  
  // Delivery status
  channel: text("channel").notNull(), // "in_app", "sms", "email", "push"
  deliveredAt: timestamp("delivered_at"),
  readAt: timestamp("read_at"),
  
  // Engagement tracking
  clicked: boolean("clicked").default(false),
  dismissed: boolean("dismissed").default(false),
  rating: integer("rating"), // 1-5 star rating
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("engagement_delivery_user_idx").on(table.userId),
  index("engagement_delivery_content_idx").on(table.contentId),
]);

// ============================================
// TIER 2: COMPETITIVE MOATS - Community Features
// ============================================

export const communityPosts = pgTable("community_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  
  // Post content
  type: text("type").notNull(), // "success_story", "forum", "question", "support"
  title: text("title").notNull(),
  content: text("content").notNull(),
  language: varchar("language").notNull().default("en"),
  
  // Privacy
  isAnonymous: boolean("is_anonymous").default(false),
  authorDisplayName: text("author_display_name"), // shown when anonymous
  
  // Moderation
  status: text("status").notNull().default("pending"), // "pending", "approved", "rejected", "flagged"
  moderatedBy: text("moderated_by"),
  moderatedAt: timestamp("moderated_at"),
  moderationNotes: text("moderation_notes"),
  
  // Engagement
  likesCount: integer("likes_count").notNull().default(0),
  commentsCount: integer("comments_count").notNull().default(0),
  viewsCount: integer("views_count").notNull().default(0),
  
  // Category/tags
  category: text("category"), // e.g., "payment_success", "health_journey", "tips"
  tags: text("tags").array(),
  
  // Featured/pinned
  isFeatured: boolean("is_featured").default(false),
  isPinned: boolean("is_pinned").default(false),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("community_post_user_idx").on(table.userId),
  index("community_post_type_idx").on(table.type),
  index("community_post_status_idx").on(table.status),
  index("community_post_language_idx").on(table.language),
]);

export const communityComments = pgTable("community_comments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  postId: varchar("post_id").notNull(),
  userId: varchar("user_id").notNull(),
  parentId: varchar("parent_id"), // for nested replies
  
  // Content
  content: text("content").notNull(),
  isAnonymous: boolean("is_anonymous").default(false),
  
  // Moderation
  status: text("status").notNull().default("approved"), // "approved", "flagged", "removed"
  
  // Engagement
  likesCount: integer("likes_count").notNull().default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("community_comment_post_idx").on(table.postId),
  index("community_comment_user_idx").on(table.userId),
  index("community_comment_parent_idx").on(table.parentId),
]);

export const communityChallenges = pgTable("community_challenges", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Challenge details
  title: text("title").notNull(),
  description: text("description").notNull(),
  type: text("type").notNull(), // "health", "savings", "engagement", "referral"
  
  // Goals
  goalType: text("goal_type").notNull(), // "count", "amount", "streak"
  goalValue: integer("goal_value").notNull(), // e.g., 30 for "30 days", 5000 for $50.00
  goalUnit: text("goal_unit"), // "days", "dollars", "actions"
  
  // Rewards
  rewardAmount: integer("reward_amount").notNull(), // in cents for wallet credit
  rewardDescription: text("reward_description"),
  
  // Timing
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  
  // Status
  isActive: boolean("is_active").default(true),
  maxParticipants: integer("max_participants"),
  currentParticipants: integer("current_participants").notNull().default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("challenge_type_idx").on(table.type),
  index("challenge_active_idx").on(table.isActive),
]);

export const userChallenges = pgTable("user_challenges", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  challengeId: varchar("challenge_id").notNull(),
  
  // Progress
  currentProgress: integer("current_progress").notNull().default(0),
  completedAt: timestamp("completed_at"),
  
  // Reward
  rewardClaimed: boolean("reward_claimed").default(false),
  rewardClaimedAt: timestamp("reward_claimed_at"),
  rewardTransactionId: varchar("reward_transaction_id"),
  
  enrolledAt: timestamp("enrolled_at").defaultNow(),
}, (table) => [
  index("user_challenge_user_idx").on(table.userId),
  index("user_challenge_challenge_idx").on(table.challengeId),
  unique("user_challenge_unique").on(table.userId, table.challengeId),
]);

// ============================================
// TIER 2: COMPETITIVE MOATS - Gamification & Achievements
// ============================================

export const achievementDefinitions = pgTable("achievement_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Achievement details
  name: text("name").notNull(),
  description: text("description").notNull(),
  icon: text("icon"), // icon name or URL
  category: text("category").notNull(), // "payment", "engagement", "referral", "health", "community"
  
  // Requirements
  requirementType: text("requirement_type").notNull(), // "count", "amount", "streak", "milestone"
  requirementValue: integer("requirement_value").notNull(),
  requirementUnit: text("requirement_unit"), // "payments", "dollars", "days", "referrals"
  
  // Rewards
  rewardAmount: integer("reward_amount").notNull().default(0), // wallet credit in cents
  badgeLevel: text("badge_level"), // "bronze", "silver", "gold", "platinum"
  
  // Ordering
  displayOrder: integer("display_order").notNull().default(0),
  isActive: boolean("is_active").default(true),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("achievement_def_category_idx").on(table.category),
]);

export const userAchievements = pgTable("user_achievements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  achievementId: varchar("achievement_id").notNull(),
  
  // Progress
  currentProgress: integer("current_progress").notNull().default(0),
  completedAt: timestamp("completed_at"),
  
  // Reward
  rewardClaimed: boolean("reward_claimed").default(false),
  rewardClaimedAt: timestamp("reward_claimed_at"),
  rewardTransactionId: varchar("reward_transaction_id"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("user_achievement_user_idx").on(table.userId),
  index("user_achievement_achievement_idx").on(table.achievementId),
  unique("user_achievement_unique").on(table.userId, table.achievementId),
]);

export const incentiveLedger = pgTable("incentive_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  walletId: varchar("wallet_id"),
  
  // Transaction details
  type: text("type").notNull(), // "referral_bonus", "achievement_reward", "challenge_reward", "engagement_bonus"
  amount: integer("amount").notNull(), // in cents
  description: text("description"),
  
  // Source reference
  sourceType: text("source_type"), // "referral", "achievement", "challenge", "engagement"
  sourceId: varchar("source_id"),
  
  // Status
  status: text("status").notNull().default("pending"), // "pending", "credited", "expired"
  creditedAt: timestamp("credited_at"),
  walletTransactionId: varchar("wallet_transaction_id"),
  
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("incentive_ledger_user_idx").on(table.userId),
  index("incentive_ledger_type_idx").on(table.type),
  index("incentive_ledger_status_idx").on(table.status),
]);

// ============================================
// TIER 2: COMPETITIVE MOATS - Financial Transparency & Resources
// ============================================

export const financialResources = pgTable("financial_resources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Content
  title: text("title").notNull(),
  content: text("content").notNull(),
  category: text("category").notNull(), // "budgeting", "insurance", "billing", "payment_tips", "rights"
  language: varchar("language").notNull().default("en"),
  
  // Targeting
  readingLevel: text("reading_level").default("8th_grade"), // simplified language
  targetAudience: text("target_audience"),
  
  // Media
  thumbnailUrl: text("thumbnail_url"),
  videoUrl: text("video_url"),
  
  // Status
  isActive: boolean("is_active").default(true),
  displayOrder: integer("display_order").notNull().default(0),
  
  // Engagement
  viewsCount: integer("views_count").notNull().default(0),
  helpfulCount: integer("helpful_count").notNull().default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("financial_resource_category_idx").on(table.category),
  index("financial_resource_language_idx").on(table.language),
]);

// ============================================
// TIER 2: COMPETITIVE MOATS - Wallet Ambassador Program
// ============================================

// Ambassador status enum
export const ambassadorStatusEnum = pgEnum("ambassador_status", [
  "pending",      // Application submitted, awaiting review
  "approved",     // Approved and active
  "denied",       // Application denied by admin
  "suspended",    // Temporarily suspended
  "inactive",     // No longer active
]);

// Ambassador tier based on performance
export const ambassadorTierEnum = pgEnum("ambassador_tier", [
  "bronze",       // New ambassadors (0-10 enrollments)
  "silver",       // Intermediate (11-50 enrollments)
  "gold",         // High performers (51-100 enrollments)
  "platinum",     // Top performers (100+ enrollments)
]);

export const ambassadors = pgTable("ambassadors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(), // Link to user account
  
  // Personal info
  displayName: text("display_name").notNull(),
  bio: text("bio"),
  profilePhotoUrl: text("profile_photo_url"),
  
  // Languages spoken (for peer education)
  languages: text("languages").array().notNull().default(sql`'{en}'::text[]`), // "en", "es", "so", "hmn"
  
  // Contact & location
  phone: varchar("phone"),
  preferredContactMethod: text("preferred_contact_method").default("email"), // "email", "phone", "sms"
  serviceArea: text("service_area"), // Geographic area they serve (e.g., "Minneapolis - Cedar Riverside")
  
  // Compensation settings
  hourlyRate: integer("hourly_rate").notNull().default(1750), // $17.50/hour default (in cents)
  enrollmentBonus: integer("enrollment_bonus").notNull().default(500), // $5 per enrollment bonus (in cents)
  
  // Status & tier
  status: ambassadorStatusEnum("status").notNull().default("pending"),
  tier: ambassadorTierEnum("tier").notNull().default("bronze"),
  
  // Performance statistics
  totalEnrollments: integer("total_enrollments").notNull().default(0),
  totalHoursWorked: integer("total_hours_worked").notNull().default(0), // in minutes
  totalEarnings: integer("total_earnings").notNull().default(0), // in cents
  totalEventsHosted: integer("total_events_hosted").notNull().default(0),
  
  // Referral code link (each ambassador gets a referral code)
  referralCodeId: varchar("referral_code_id"),
  
  // Training & certification
  trainingCompletedAt: timestamp("training_completed_at"),
  certificationExpiresAt: timestamp("certification_expires_at"),
  
  // Metadata
  appliedAt: timestamp("applied_at").defaultNow(),
  approvedAt: timestamp("approved_at"),
  approvedBy: varchar("approved_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("ambassador_user_idx").on(table.userId),
  index("ambassador_status_idx").on(table.status),
  index("ambassador_tier_idx").on(table.tier),
  index("ambassador_referral_idx").on(table.referralCodeId),
]);

// Time logs for tracking ambassador hours worked
export const ambassadorTimeLogs = pgTable("ambassador_time_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ambassadorId: varchar("ambassador_id").notNull(),
  
  // Time tracking
  date: timestamp("date").notNull(),
  hoursWorked: integer("hours_worked").notNull(), // in minutes
  description: text("description"),
  
  // Activity type
  activityType: text("activity_type").notNull(), // "community_event", "outreach", "training", "follow_up", "admin"
  
  // Compensation calculation
  hourlyRate: integer("hourly_rate").notNull(), // rate at time of work (in cents)
  totalCompensation: integer("total_compensation").notNull(), // calculated total (in cents)
  
  // Payment status
  isPaid: boolean("is_paid").default(false),
  paidAt: timestamp("paid_at"),
  paymentReference: varchar("payment_reference"),
  
  // Approval workflow
  isApproved: boolean("is_approved").default(false),
  approvedBy: varchar("approved_by"),
  approvedAt: timestamp("approved_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("time_log_ambassador_idx").on(table.ambassadorId),
  index("time_log_date_idx").on(table.date),
  index("time_log_paid_idx").on(table.isPaid),
  index("time_log_approved_idx").on(table.isApproved),
]);

// Community events managed by ambassadors
export const communityEvents = pgTable("community_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Event details
  title: text("title").notNull(),
  description: text("description"),
  eventType: text("event_type").notNull(), // "tabling", "workshop", "health_fair", "community_meeting", "online_webinar"
  
  // Location
  location: text("location"),
  address: text("address"),
  isVirtual: boolean("is_virtual").default(false),
  virtualMeetingUrl: text("virtual_meeting_url"),
  
  // Timing
  startTime: timestamp("start_time").notNull(),
  endTime: timestamp("end_time").notNull(),
  timezone: varchar("timezone").default("America/Chicago"),
  
  // Ambassador assignment
  hostAmbassadorId: varchar("host_ambassador_id").notNull(),
  coHostAmbassadorIds: text("co_host_ambassador_ids").array().default(sql`'{}'::text[]`),
  
  // Target audience
  targetLanguages: text("target_languages").array().default(sql`'{en}'::text[]`),
  targetCommunity: text("target_community"), // "somali", "hmong", "latino", "general"
  
  // Capacity & registration
  maxAttendees: integer("max_attendees"),
  registeredCount: integer("registered_count").notNull().default(0),
  attendedCount: integer("attended_count").notNull().default(0),
  
  // Results tracking
  enrollmentsFromEvent: integer("enrollments_from_event").notNull().default(0),
  materialsDistributed: integer("materials_distributed").notNull().default(0),
  
  // Status
  status: text("status").notNull().default("scheduled"), // "scheduled", "in_progress", "completed", "cancelled"
  cancellationReason: text("cancellation_reason"),
  
  // Notes & follow-up
  notes: text("notes"),
  followUpRequired: boolean("follow_up_required").default(false),
  followUpNotes: text("follow_up_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("community_event_host_idx").on(table.hostAmbassadorId),
  index("community_event_date_idx").on(table.startTime),
  index("community_event_status_idx").on(table.status),
  index("community_event_type_idx").on(table.eventType),
]);

// Track enrollments attributed to ambassadors
export const ambassadorEnrollments = pgTable("ambassador_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ambassadorId: varchar("ambassador_id").notNull(),
  
  // Enrolled user/wallet
  enrolledUserId: varchar("enrolled_user_id").notNull(),
  enrolledWalletId: varchar("enrolled_wallet_id"),
  
  // Attribution source
  source: text("source").notNull(), // "referral_code", "community_event", "direct_outreach"
  eventId: varchar("event_id"), // If from an event
  referralId: varchar("referral_id"), // If from referral system
  
  // Enrollment details
  enrollmentDate: timestamp("enrollment_date").defaultNow(),
  
  // Bonus tracking
  bonusAmount: integer("bonus_amount").notNull(), // in cents
  bonusPaid: boolean("bonus_paid").default(false),
  bonusPaidAt: timestamp("bonus_paid_at"),
  paymentReference: varchar("payment_reference"),
  
  // Qualification (bonus paid when user makes first payment)
  isQualified: boolean("is_qualified").default(false),
  qualifiedAt: timestamp("qualified_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("ambassador_enrollment_ambassador_idx").on(table.ambassadorId),
  index("ambassador_enrollment_user_idx").on(table.enrolledUserId),
  index("ambassador_enrollment_event_idx").on(table.eventId),
  index("ambassador_enrollment_qualified_idx").on(table.isQualified),
]);

// ============================================
// Insert Schemas and Types for TIER 2 Features
// ============================================

export const insertAmbassadorSchema = createInsertSchema(ambassadors).omit({
  id: true, createdAt: true, updatedAt: true, appliedAt: true, approvedAt: true, approvedBy: true,
  totalEnrollments: true, totalHoursWorked: true, totalEarnings: true, totalEventsHosted: true,
});
export type InsertAmbassador = z.infer<typeof insertAmbassadorSchema>;
export type Ambassador = typeof ambassadors.$inferSelect;

export const insertAmbassadorTimeLogSchema = createInsertSchema(ambassadorTimeLogs).omit({
  id: true, createdAt: true, isPaid: true, paidAt: true, paymentReference: true,
  isApproved: true, approvedBy: true, approvedAt: true,
});
export type InsertAmbassadorTimeLog = z.infer<typeof insertAmbassadorTimeLogSchema>;
export type AmbassadorTimeLog = typeof ambassadorTimeLogs.$inferSelect;

export const insertCommunityEventSchema = createInsertSchema(communityEvents).omit({
  id: true, createdAt: true, updatedAt: true, registeredCount: true, attendedCount: true,
  enrollmentsFromEvent: true, materialsDistributed: true,
});
export type InsertCommunityEvent = z.infer<typeof insertCommunityEventSchema>;
export type CommunityEvent = typeof communityEvents.$inferSelect;

export const insertAmbassadorEnrollmentSchema = createInsertSchema(ambassadorEnrollments).omit({
  id: true, createdAt: true, bonusPaid: true, bonusPaidAt: true, paymentReference: true,
  isQualified: true, qualifiedAt: true,
});
export type InsertAmbassadorEnrollment = z.infer<typeof insertAmbassadorEnrollmentSchema>;
export type AmbassadorEnrollment = typeof ambassadorEnrollments.$inferSelect;

export const insertPreCareSubscriptionSchema = createInsertSchema(preCareSubscriptions).omit({
  id: true, createdAt: true, updatedAt: true, totalContributed: true, paymentsCount: true,
  cancelledAt: true, pausedAt: true,
});
export type InsertPreCareSubscription = z.infer<typeof insertPreCareSubscriptionSchema>;
export type PreCareSubscription = typeof preCareSubscriptions.$inferSelect;

export const insertPreCareSubscriptionPaymentSchema = createInsertSchema(preCareSubscriptionPayments).omit({
  id: true, createdAt: true,
});
export type InsertPreCareSubscriptionPayment = z.infer<typeof insertPreCareSubscriptionPaymentSchema>;
export type PreCareSubscriptionPayment = typeof preCareSubscriptionPayments.$inferSelect;

export const insertReferralCodeSchema = createInsertSchema(referralCodes).omit({
  id: true, createdAt: true, timesUsed: true, totalBonusesEarned: true,
});
export type InsertReferralCode = z.infer<typeof insertReferralCodeSchema>;
export type ReferralCode = typeof referralCodes.$inferSelect;

export const insertReferralSchema = createInsertSchema(referrals).omit({
  id: true, createdAt: true, qualifiedAt: true, bonusPaidAt: true,
});
export type InsertReferral = z.infer<typeof insertReferralSchema>;
export type Referral = typeof referrals.$inferSelect;

export const insertEngagementContentSchema = createInsertSchema(engagementContent).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertEngagementContent = z.infer<typeof insertEngagementContentSchema>;
export type EngagementContent = typeof engagementContent.$inferSelect;

export const insertEngagementDeliverySchema = createInsertSchema(engagementDeliveries).omit({
  id: true, createdAt: true,
});
export type InsertEngagementDelivery = z.infer<typeof insertEngagementDeliverySchema>;
export type EngagementDelivery = typeof engagementDeliveries.$inferSelect;

export const insertCommunityPostSchema = createInsertSchema(communityPosts).omit({
  id: true, createdAt: true, updatedAt: true, likesCount: true, commentsCount: true, viewsCount: true,
  moderatedBy: true, moderatedAt: true,
});
export type InsertCommunityPost = z.infer<typeof insertCommunityPostSchema>;
export type CommunityPost = typeof communityPosts.$inferSelect;

export const insertCommunityCommentSchema = createInsertSchema(communityComments).omit({
  id: true, createdAt: true, updatedAt: true, likesCount: true,
});
export type InsertCommunityComment = z.infer<typeof insertCommunityCommentSchema>;
export type CommunityComment = typeof communityComments.$inferSelect;

export const insertCommunityChallengeSchema = createInsertSchema(communityChallenges).omit({
  id: true, createdAt: true, currentParticipants: true,
});
export type InsertCommunityChallenge = z.infer<typeof insertCommunityChallengeSchema>;
export type CommunityChallenge = typeof communityChallenges.$inferSelect;

export const insertUserChallengeSchema = createInsertSchema(userChallenges).omit({
  id: true, enrolledAt: true, currentProgress: true, completedAt: true, rewardClaimed: true,
  rewardClaimedAt: true, rewardTransactionId: true,
});
export type InsertUserChallenge = z.infer<typeof insertUserChallengeSchema>;
export type UserChallenge = typeof userChallenges.$inferSelect;

export const insertAchievementDefinitionSchema = createInsertSchema(achievementDefinitions).omit({
  id: true, createdAt: true,
});
export type InsertAchievementDefinition = z.infer<typeof insertAchievementDefinitionSchema>;
export type AchievementDefinition = typeof achievementDefinitions.$inferSelect;

export const insertUserAchievementSchema = createInsertSchema(userAchievements).omit({
  id: true, createdAt: true, currentProgress: true, completedAt: true, rewardClaimed: true,
  rewardClaimedAt: true, rewardTransactionId: true,
});
export type InsertUserAchievement = z.infer<typeof insertUserAchievementSchema>;
export type UserAchievement = typeof userAchievements.$inferSelect;

export const insertIncentiveLedgerSchema = createInsertSchema(incentiveLedger).omit({
  id: true, createdAt: true, creditedAt: true, walletTransactionId: true,
});
export type InsertIncentiveLedger = z.infer<typeof insertIncentiveLedgerSchema>;
export type IncentiveLedgerEntry = typeof incentiveLedger.$inferSelect;

export const insertFinancialResourceSchema = createInsertSchema(financialResources).omit({
  id: true, createdAt: true, updatedAt: true, viewsCount: true, helpfulCount: true,
});
export type InsertFinancialResource = z.infer<typeof insertFinancialResourceSchema>;
export type FinancialResource = typeof financialResources.$inferSelect;

// ============================================
// Revenue Cycle & ROI Performance Management
// ============================================

// Hospital Partner Locations - Multi-location support for national hospital networks
export const hospitalPartnerLocations = pgTable("hospital_partner_locations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(), // parent hospital partner
  
  // Location details
  locationName: text("location_name").notNull(), // e.g., "Children's Hospital - Los Angeles"
  locationCode: varchar("location_code").unique(), // unique identifier e.g., "CH-LA-001"
  facilityId: varchar("facility_id"), // optional link to facilities table
  
  // Address
  address: text("address"),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zipCode: text("zip_code"),
  region: text("region"), // geographic region for grouping (e.g., "West", "Midwest", "Northeast")
  
  // Contact
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  
  // Status
  isActive: boolean("is_active").default(true),
  onboardedAt: timestamp("onboarded_at"),
  
  // Baseline metrics (pre-HealthWallet benchmarks for ROI calculation)
  baselineDaysSalesOutstanding: integer("baseline_days_sales_outstanding"), // DSO before platform
  baselineDenialRate: doublePrecision("baseline_denial_rate"), // percentage (0-100)
  baselineBadDebtRate: doublePrecision("baseline_bad_debt_rate"), // percentage (0-100)
  baselineCollectionRate: doublePrecision("baseline_collection_rate"), // percentage (0-100)
  baselineAvgTimeToPayment: integer("baseline_avg_time_to_payment"), // days
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("partner_location_partner_idx").on(table.partnerId),
  index("partner_location_city_state_idx").on(table.city, table.state),
  index("partner_location_region_idx").on(table.region),
]);

// Revenue Cycle Snapshots - Daily/weekly aggregated metrics per location
export const revenueCycleSnapshots = pgTable("revenue_cycle_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(),
  locationId: varchar("location_id"), // null = aggregate for all locations
  
  // Time period
  snapshotDate: timestamp("snapshot_date").notNull(),
  periodType: text("period_type").notNull().default("daily"), // "daily", "weekly", "monthly"
  
  // Cash Acceleration Metrics
  totalWalletFundedRevenue: integer("total_wallet_funded_revenue").default(0), // in cents
  totalTraditionalRevenue: integer("total_traditional_revenue").default(0), // in cents
  daysSalesOutstanding: integer("days_sales_outstanding"), // current DSO
  avgTimeToPayment: integer("avg_time_to_payment"), // days from service to payment
  
  // Claims Metrics
  totalClaimsSubmitted: integer("total_claims_submitted").default(0),
  totalClaimsApproved: integer("total_claims_approved").default(0),
  totalClaimsDenied: integer("total_claims_denied").default(0),
  totalClaimsPending: integer("total_claims_pending").default(0),
  denialRate: doublePrecision("denial_rate"), // percentage
  cleanClaimRate: doublePrecision("clean_claim_rate"), // percentage of claims approved first time
  
  // Collection Metrics
  totalBilled: integer("total_billed").default(0), // in cents
  totalCollected: integer("total_collected").default(0), // in cents
  collectionRate: doublePrecision("collection_rate"), // percentage
  badDebtWriteOff: integer("bad_debt_write_off").default(0), // in cents
  
  // Wallet-Specific Metrics
  walletPaymentRequestsCreated: integer("wallet_payment_requests_created").default(0),
  walletPaymentRequestsApproved: integer("wallet_payment_requests_approved").default(0),
  walletPaymentRequestsCompleted: integer("wallet_payment_requests_completed").default(0),
  avgWalletPaymentAmount: integer("avg_wallet_payment_amount"), // in cents
  walletUtilizationRate: doublePrecision("wallet_utilization_rate"), // percentage of eligible patients using wallets
  
  // Patient Financial Metrics
  patientsServed: integer("patients_served").default(0),
  patientsWithWallets: integer("patients_with_wallets").default(0),
  patientsOnPaymentPlans: integer("patients_on_payment_plans").default(0),
  totalPatientAssistance: integer("total_patient_assistance").default(0), // community fund allocations in cents
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("revenue_snapshot_partner_idx").on(table.partnerId),
  index("revenue_snapshot_location_idx").on(table.locationId),
  index("revenue_snapshot_date_idx").on(table.snapshotDate),
  unique("revenue_snapshot_unique").on(table.partnerId, table.locationId, table.snapshotDate, table.periodType),
]);

// ROI Assessments - Periodic ROI calculations with assumptions
export const roiAssessments = pgTable("roi_assessments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(),
  locationId: varchar("location_id"), // null = national/all locations
  
  // Assessment period
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  assessmentType: text("assessment_type").notNull().default("quarterly"), // "monthly", "quarterly", "annual"
  
  // Revenue Impact
  walletFundedCollections: integer("wallet_funded_collections").default(0), // in cents
  incrementalRevenue: integer("incremental_revenue").default(0), // revenue above baseline
  avoidedBadDebt: integer("avoided_bad_debt").default(0), // debt recovery through wallets
  acceleratedCashFlow: integer("accelerated_cash_flow").default(0), // value of faster payments
  
  // Cost Savings
  reducedDenialCosts: integer("reduced_denial_costs").default(0), // saved appeals costs
  reducedCollectionCosts: integer("reduced_collection_costs").default(0), // saved A/R follow-up
  adminEfficiencySavings: integer("admin_efficiency_savings").default(0), // staff time savings
  
  // Platform Costs
  platformFees: integer("platform_fees").default(0), // HealthWallet fees
  implementationCosts: integer("implementation_costs").default(0), // one-time setup
  operationalCosts: integer("operational_costs").default(0), // ongoing costs
  
  // ROI Calculations
  totalBenefits: integer("total_benefits").default(0), // sum of revenue + savings
  totalCosts: integer("total_costs").default(0), // sum of all costs
  netValue: integer("net_value").default(0), // benefits - costs
  roiPercentage: doublePrecision("roi_percentage"), // (benefits - costs) / costs * 100
  paybackPeriodMonths: integer("payback_period_months"), // time to break even
  
  // Qualitative Value (stored as text/notes)
  qualitativeNotes: text("qualitative_notes"), // patient satisfaction, time-to-therapy, etc.
  
  // Assumptions used in calculation
  assumptions: jsonb("assumptions"), // JSON object with calculation assumptions
  
  // Status
  status: text("status").notNull().default("draft"), // "draft", "final", "approved"
  approvedBy: varchar("approved_by"),
  approvedAt: timestamp("approved_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
}, (table) => [
  index("roi_assessment_partner_idx").on(table.partnerId),
  index("roi_assessment_location_idx").on(table.locationId),
  index("roi_assessment_period_idx").on(table.periodStart, table.periodEnd),
]);

// Wallet Payment Allocations - Bridge between wallet payments and claims/services
export const walletPaymentAllocations = pgTable("wallet_payment_allocations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletTransactionId: varchar("wallet_transaction_id").notNull(), // link to wallet_transactions
  partnerId: varchar("partner_id").notNull(),
  locationId: varchar("location_id"), // specific location if applicable
  
  // Service/Claim details
  claimNumber: varchar("claim_number"), // hospital claim/billing number
  serviceDate: timestamp("service_date"),
  serviceType: text("service_type"), // "pharmacy", "lab", "imaging", "procedure", "visit"
  serviceDescription: text("service_description"),
  
  // Amount breakdown
  totalServiceCost: integer("total_service_cost").notNull(), // full service cost in cents
  amountFromWallet: integer("amount_from_wallet").notNull(), // portion paid from wallet
  amountFromInsurance: integer("amount_from_insurance").default(0), // insurance portion
  amountPatientOwes: integer("amount_patient_owes").default(0), // remaining patient responsibility
  
  // Status
  status: text("status").notNull().default("pending"), // "pending", "applied", "reconciled"
  reconciledAt: timestamp("reconciled_at"),
  reconciledBy: varchar("reconciled_by"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("wallet_allocation_transaction_idx").on(table.walletTransactionId),
  index("wallet_allocation_partner_idx").on(table.partnerId),
  index("wallet_allocation_location_idx").on(table.locationId),
  index("wallet_allocation_claim_idx").on(table.claimNumber),
]);

// Insert schemas and types for Revenue Cycle tables
export const insertHospitalPartnerLocationSchema = createInsertSchema(hospitalPartnerLocations).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertHospitalPartnerLocation = z.infer<typeof insertHospitalPartnerLocationSchema>;
export type HospitalPartnerLocation = typeof hospitalPartnerLocations.$inferSelect;

export const insertRevenueCycleSnapshotSchema = createInsertSchema(revenueCycleSnapshots).omit({
  id: true, createdAt: true,
});
export type InsertRevenueCycleSnapshot = z.infer<typeof insertRevenueCycleSnapshotSchema>;
export type RevenueCycleSnapshot = typeof revenueCycleSnapshots.$inferSelect;

export const insertRoiAssessmentSchema = createInsertSchema(roiAssessments).omit({
  id: true, createdAt: true, updatedAt: true, approvedAt: true,
});
export type InsertRoiAssessment = z.infer<typeof insertRoiAssessmentSchema>;
export type RoiAssessment = typeof roiAssessments.$inferSelect;

export const insertWalletPaymentAllocationSchema = createInsertSchema(walletPaymentAllocations).omit({
  id: true, createdAt: true, updatedAt: true, reconciledAt: true,
});
export type InsertWalletPaymentAllocation = z.infer<typeof insertWalletPaymentAllocationSchema>;
export type WalletPaymentAllocation = typeof walletPaymentAllocations.$inferSelect;

// ============================================
// Employer Partnership System
// ============================================

export const employerPartnerStatusEnum = pgEnum("employer_partner_status", ["pending", "active", "suspended", "terminated"]);
export const employerPartnerTierEnum = pgEnum("employer_partner_tier", ["starter", "growth", "enterprise"]);
export const employerContributionTypeEnum = pgEnum("employer_contribution_type", ["one_time", "recurring", "match"]);

export const employerPartners = pgTable("employer_partners", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  companyName: text("company_name").notNull(),
  companyCode: varchar("company_code").unique(),
  industry: text("industry"),
  companySize: text("company_size"),
  taxId: text("tax_id"),
  
  contactName: text("contact_name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone"),
  contactTitle: text("contact_title"),
  
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  
  status: text("status").notNull().default("pending"),
  tier: text("tier").notNull().default("bronze"),
  
  matchPercent: integer("match_percent").default(0),
  matchCapCents: integer("match_cap_cents").default(0),
  annualBudgetCents: integer("annual_budget_cents").default(0),
  annualBudgetUsedCents: integer("annual_budget_used_cents").default(0),
  
  contributionFrequency: text("contribution_frequency").default("monthly"),
  recurringAmountCents: integer("recurring_amount_cents").default(0),
  
  totalContributedCents: integer("total_contributed_cents").default(0),
  totalEmployeesEnrolled: integer("total_employees_enrolled").default(0),
  
  contractStartDate: timestamp("contract_start_date"),
  contractEndDate: timestamp("contract_end_date"),
  notes: text("notes"),
  
  isSmallBusiness: boolean("is_small_business").default(false),
  simplifiedPlan: text("simplified_plan"),
  monthlyPerEmployeeCents: integer("monthly_per_employee_cents").default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: text("created_by"),
}, (table) => [
  index("employer_partner_status_idx").on(table.status),
  index("employer_partner_code_idx").on(table.companyCode),
  index("employer_partner_company_idx").on(table.companyName),
]);

export const employerEmployees = pgTable("employer_employees", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  employerId: varchar("employer_id").notNull(),
  userId: varchar("user_id"),
  walletId: varchar("wallet_id"),
  
  employeeEmail: text("employee_email").notNull(),
  employeeName: text("employee_name").notNull(),
  employeeId: text("employee_id"),
  department: text("department"),
  
  status: text("status").notNull().default("invited"),
  enrolledAt: timestamp("enrolled_at"),
  
  totalReceivedCents: integer("total_received_cents").default(0),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("employer_employee_employer_idx").on(table.employerId),
  index("employer_employee_user_idx").on(table.userId),
  index("employer_employee_wallet_idx").on(table.walletId),
  index("employer_employee_email_idx").on(table.employeeEmail),
]);

export const employerContributions = pgTable("employer_contributions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  employerId: varchar("employer_id").notNull(),
  employeeId: varchar("employee_id"),
  walletId: varchar("wallet_id"),
  walletTransactionId: varchar("wallet_transaction_id"),
  
  type: text("type").notNull().default("one_time"),
  amountCents: integer("amount_cents").notNull(),
  description: text("description"),
  
  status: text("status").notNull().default("completed"),
  
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("employer_contribution_employer_idx").on(table.employerId),
  index("employer_contribution_employee_idx").on(table.employeeId),
  index("employer_contribution_wallet_idx").on(table.walletId),
  index("employer_contribution_type_idx").on(table.type),
]);

// Insert schemas
export const insertEmployerPartnerSchema = createInsertSchema(employerPartners).omit({
  id: true, createdAt: true, updatedAt: true, totalContributedCents: true, totalEmployeesEnrolled: true, annualBudgetUsedCents: true,
});
export const insertEmployerEmployeeSchema = createInsertSchema(employerEmployees).omit({
  id: true, createdAt: true, updatedAt: true, totalReceivedCents: true, enrolledAt: true,
});
export const insertEmployerContributionSchema = createInsertSchema(employerContributions).omit({
  id: true, createdAt: true,
});

// Types
export type InsertEmployerPartner = z.infer<typeof insertEmployerPartnerSchema>;
export type EmployerPartner = typeof employerPartners.$inferSelect;
export type InsertEmployerEmployee = z.infer<typeof insertEmployerEmployeeSchema>;
export type EmployerEmployee = typeof employerEmployees.$inferSelect;
export type InsertEmployerContribution = z.infer<typeof insertEmployerContributionSchema>;
export type EmployerContribution = typeof employerContributions.$inferSelect;

// Feature Flags
export const featureFlagsTable = pgTable("feature_flags", {
  key: varchar("key").primaryKey(),
  enabled: boolean("enabled").notNull().default(false),
  label: varchar("label").notNull(),
  description: text("description").notNull().default(""),
  category: varchar("category").notNull().default("general"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertFeatureFlagSchema = createInsertSchema(featureFlagsTable).omit({ updatedAt: true });
export type InsertFeatureFlag = z.infer<typeof insertFeatureFlagSchema>;
export type FeatureFlag = typeof featureFlagsTable.$inferSelect;

// Success Stories - admin-managed community impact stories for landing page
export const successStories = pgTable("success_stories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  location: text("location").notNull(),
  condition: text("condition").notNull(),
  conditionColor: text("condition_color").notNull().default("bg-blue-500/10 text-blue-600 dark:text-blue-400"),
  quote: text("quote").notNull(),
  careAccessed: text("care_accessed").notNull(),
  fundsRaised: text("funds_raised").notNull(),
  contributors: integer("contributors").notNull(),
  walletType: text("wallet_type").notNull(),
  timeframe: text("timeframe").notNull(),
  iconType: text("icon_type").notNull().default("stethoscope"),
  displayOrder: integer("display_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  isFeatured: boolean("is_featured").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSuccessStorySchema = createInsertSchema(successStories).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertSuccessStory = z.infer<typeof insertSuccessStorySchema>;
export type SuccessStory = typeof successStories.$inferSelect;

export const emergencyCareStatusEnum = pgEnum("emergency_care_status", [
  "pending",
  "active",
  "partially_funded",
  "fully_funded",
  "payment_plan_created",
  "closed",
]);

export const emergencyCareRequests = pgTable("emergency_care_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(),
  walletId: varchar("wallet_id"),

  patientName: text("patient_name").notNull(),
  patientEmail: text("patient_email"),
  patientPhone: text("patient_phone"),

  serviceDescription: text("service_description").notNull(),
  serviceDate: timestamp("service_date").notNull(),
  serviceType: text("service_type").notNull(),
  diagnosisCode: text("diagnosis_code"),

  totalCost: integer("total_cost").notNull(),
  amountFunded: integer("amount_funded").notNull().default(0),

  status: text("status").notNull().default("pending"),

  shareToken: varchar("share_token").unique(),
  claimToken: varchar("claim_token").unique(),
  claimedByUserId: varchar("claimed_by_user_id"),
  claimedAt: timestamp("claimed_at"),

  paymentPlanId: varchar("payment_plan_id"),

  urgencyLevel: text("urgency_level").notNull().default("urgent"),
  hospitalNotes: text("hospital_notes"),
  consentToShare: boolean("consent_to_share").notNull().default(false),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: text("created_by"),
}, (table) => [
  index("ecr_partner_idx").on(table.partnerId),
  index("ecr_wallet_idx").on(table.walletId),
  index("ecr_status_idx").on(table.status),
  index("ecr_share_token_idx").on(table.shareToken),
  index("ecr_claim_token_idx").on(table.claimToken),
]);

export const insertEmergencyCareRequestSchema = createInsertSchema(emergencyCareRequests).omit({
  id: true, createdAt: true, updatedAt: true, amountFunded: true, walletId: true,
  shareToken: true, claimToken: true, claimedByUserId: true, claimedAt: true, paymentPlanId: true,
});
export type InsertEmergencyCareRequest = z.infer<typeof insertEmergencyCareRequestSchema>;
export type EmergencyCareRequest = typeof emergencyCareRequests.$inferSelect;

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").default(false).notNull(),
  actionUrl: text("action_url"),
  metadata: text("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("notif_user_idx").on(table.userId),
  index("notif_user_read_idx").on(table.userId, table.isRead),
]);

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true, createdAt: true, isRead: true,
});
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

export const thankYouMessages = pgTable("thank_you_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletTransactionId: varchar("wallet_transaction_id").notNull().unique(),
  walletId: varchar("wallet_id").notNull(),
  senderUserId: varchar("sender_user_id").notNull(),
  contributorName: text("contributor_name"),
  contributorEmail: text("contributor_email"),
  message: text("message").notNull(),
  sentAt: timestamp("sent_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("thank_you_wallet_idx").on(table.walletId),
  index("thank_you_sender_idx").on(table.senderUserId),
  index("thank_you_contributor_email_idx").on(table.contributorEmail),
]);

export const insertThankYouSchema = createInsertSchema(thankYouMessages).omit({
  id: true, createdAt: true, sentAt: true,
});
export type InsertThankYou = z.infer<typeof insertThankYouSchema>;
export type ThankYouMessage = typeof thankYouMessages.$inferSelect;

export const platformFees = pgTable("platform_fees", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  paymentFlowType: text("payment_flow_type").notNull(),
  grossAmount: integer("gross_amount").notNull(),
  feePercentage: doublePrecision("fee_percentage").notNull(),
  feeAmount: integer("fee_amount").notNull(),
  netAmount: integer("net_amount").notNull(),
  stripeSessionId: varchar("stripe_session_id"),
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  walletId: varchar("wallet_id"),
  communityFundId: varchar("community_fund_id"),
  conditionWalletId: varchar("condition_wallet_id"),
  emergencyRequestId: varchar("emergency_request_id"),
  contributorName: text("contributor_name"),
  contributorEmail: text("contributor_email"),
  status: text("status").notNull().default("collected"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("platform_fee_flow_type_idx").on(table.paymentFlowType),
  index("platform_fee_status_idx").on(table.status),
  index("platform_fee_created_idx").on(table.createdAt),
  index("platform_fee_wallet_idx").on(table.walletId),
  index("platform_fee_stripe_session_idx").on(table.stripeSessionId),
]);

export const insertPlatformFeeSchema = createInsertSchema(platformFees).omit({
  id: true, createdAt: true,
});
export type InsertPlatformFee = z.infer<typeof insertPlatformFeeSchema>;
export type PlatformFee = typeof platformFees.$inferSelect;

export const platformFeeRates = pgTable("platform_fee_rates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  flowType: text("flow_type").notNull().unique(),
  feePercentage: doublePrecision("fee_percentage").notNull(),
  label: text("label").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertPlatformFeeRateSchema = createInsertSchema(platformFeeRates).omit({
  id: true, updatedAt: true,
});
export type InsertPlatformFeeRate = z.infer<typeof insertPlatformFeeRateSchema>;
export type PlatformFeeRate = typeof platformFeeRates.$inferSelect;

export const marketingVideos = pgTable("marketing_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  description: text("description"),
  videoUrl: text("video_url").notNull(),
  thumbnailUrl: text("thumbnail_url"),
  category: text("category").notNull().default("how-it-works"),
  displayOrder: integer("display_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  ctaText: text("cta_text"),
  ctaLink: text("cta_link"),
  durationSeconds: integer("duration_seconds"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by"),
}, (table) => [
  index("marketing_video_active_idx").on(table.isActive),
  index("marketing_video_category_idx").on(table.category),
  index("marketing_video_order_idx").on(table.displayOrder),
]);

export const insertMarketingVideoSchema = createInsertSchema(marketingVideos).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertMarketingVideo = z.infer<typeof insertMarketingVideoSchema>;
export type MarketingVideo = typeof marketingVideos.$inferSelect;

export const demoRequests = pgTable("demo_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull(),
  company: text("company").notNull(),
  role: text("role"),
  message: text("message"),
  status: text("status").notNull().default("new"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDemoRequestSchema = createInsertSchema(demoRequests).omit({
  id: true, createdAt: true, status: true,
});
export type InsertDemoRequest = z.infer<typeof insertDemoRequestSchema>;
export type DemoRequest = typeof demoRequests.$inferSelect;

export const pilotApplications = pgTable("pilot_applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyName: text("company_name").notNull(),
  applicantName: text("applicant_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  companyType: text("company_type").notNull(),
  employeeCount: text("employee_count"),
  location: text("location"),
  interestArea: text("interest_area"),
  message: text("message"),
  status: text("status").notNull().default("new"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPilotApplicationSchema = createInsertSchema(pilotApplications).omit({
  id: true, createdAt: true, status: true,
});
export type InsertPilotApplication = z.infer<typeof insertPilotApplicationSchema>;
export type PilotApplication = typeof pilotApplications.$inferSelect;

// Hospital Partner Applications - public marketing/apply page submissions
export const hospitalPartnerApplications = pgTable("hospital_partner_applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationName: text("organization_name").notNull(),
  contactName: text("contact_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  organizationType: text("organization_type").notNull(),
  numberOfLocations: text("number_of_locations").notNull(),
  ein: text("ein").notNull(),
  message: text("message"),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertHospitalPartnerApplicationSchema = createInsertSchema(hospitalPartnerApplications).omit({
  id: true, createdAt: true, status: true,
});
export type InsertHospitalPartnerApplication = z.infer<typeof insertHospitalPartnerApplicationSchema>;
export type HospitalPartnerApplication = typeof hospitalPartnerApplications.$inferSelect;
